// Guest Check-Out Component for AAR Guest Management System
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { checkOutGuest, fetchParticipants, subscribeToSmsMessages, getGuestCheckInHistory } from '../firebase/firestoreService';
import { sendTransportationSMS } from '../firebase/functionsService';
import { getCurrentUser } from '../firebase/authService';
import { calculateDuration, formatDuration, formatDate } from '../utils/dateUtils';
import placeholderAvatar from '../assets/placeholder-avatar.svg';
import AddNoteModal from './AddNoteModal';
import { useAppBusy } from '../utils/appUpdate';
import '../styles/CheckOut.css';
import '../styles/ConfirmDialog.css';
import { readOnlyProps } from '../utils/viewAs';

// Format phone number to (XXX) XXX-XXXX format
const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  } else if (cleaned.length === 11 && cleaned[0] === '1') {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
};

// Format SMS timestamp
const formatSmsTime = (timestamp) => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const CheckOut = () => {
  const { checkInId } = useParams();
  const [checkInData, setCheckInData] = useState(null);
  const [guestData, setGuestData] = useState(null);
  const [pagerRetrieved, setPagerRetrieved] = useState(false);
  const [actualPickupTime, setActualPickupTime] = useState('');
  const [vidNumber, setVidNumber] = useState('');
  const [licPlateNumber, setLicPlateNumber] = useState('');
  const [vehicleColor, setVehicleColor] = useState('');
  const [pickupNotes, setPickupNotes] = useState('');
  const [pickupNotesOpen, setPickupNotesOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [smsMessages, setSmsMessages] = useState([]);
  const [showSmsConfirm, setShowSmsConfirm] = useState(false);
  const [sendingSms, setSendingSms] = useState(false);
  const [smsSuccess, setSmsSuccess] = useState('');
  const [smsError, setSmsError] = useState('');
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [editableMessage, setEditableMessage] = useState('');
  const [profileNotes, setProfileNotes] = useState([]);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showCheckedOutConfirm, setShowCheckedOutConfirm] = useState(false);
  const navigate = useNavigate();
  const user = getCurrentUser();
  const ro = readOnlyProps(); // disable write controls while viewing as someone

  // Hold off the idle auto-reload while the operator has anything in hand:
  // a field filled in (all start empty), a submit or SMS in flight, or a
  // dialog open — including the checked-out confirmation, which a reload
  // would dismiss before it was read.
  const formDirty = Boolean(
    pagerRetrieved || actualPickupTime || vidNumber || licPlateNumber ||
    vehicleColor || pickupNotes || notes
  );
  useAppBusy(
    'check-out',
    formDirty || submitting || sendingSms || showSmsConfirm ||
      showNoteModal || showCheckedOutConfirm
  );

  const refreshProfileNotes = () => {
    const guestId = checkInData?.guestId;
    if (!guestId || guestId === 'new' || !user?.uid) return;
    getGuestCheckInHistory(guestId, user.uid)
      .then((entries) => setProfileNotes(entries.filter((n) => n.source === 'Profile Note')))
      .catch((err) => console.warn('Could not load profile notes:', err));
  };

  // Scroll to top when error appears
  useEffect(() => {
    if (error) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [error]);

  // Real-time listener for check-in data (updates smsSent, smsReplyReceived, etc.)
  useEffect(() => {
    if (!checkInId) return;

    const checkInRef = doc(db, 'checkIns', checkInId);
    const unsubscribe = onSnapshot(checkInRef, (snap) => {
      if (snap.exists()) {
        const data = { id: snap.id, ...snap.data() };
        setCheckInData(data);

        // Load participant data on first snapshot
        if (!guestData) {
          fetchParticipants('1269').then(participantList => {
            const match = participantList.find(p =>
              p.firstName === data.guestFirstName &&
              p.lastName === data.guestLastName
            );
            if (match) setGuestData(match);
          }).catch(err => console.log('Could not load participant data:', err));
        }
      } else {
        setError('Check-in record not found.');
      }
      setLoading(false);
    }, (err) => {
      console.error('Error listening to check-in data:', err);
      setError('Failed to load check-in data. Please try again.');
      setLoading(false);
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkInId]);

  // Subscribe to SMS messages for this check-in (real-time)
  useEffect(() => {
    if (!checkInId) return;
    const unsubscribe = subscribeToSmsMessages(checkInId, (messages) => {
      setSmsMessages(messages);
    });
    return () => unsubscribe();
  }, [checkInId]);

  // Fetch profile notes (notes added outside the check-in/check-out flow) for this guest
  useEffect(() => {
    refreshProfileNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkInData?.guestId, user?.uid]);

  const handleCheckOut = async (e) => {
    e.preventDefault();

    if (checkInData.pagerNumber && !pagerRetrieved) {
      if (!window.confirm('Pager was not marked as retrieved. Are you sure you want to check out?')) {
        return;
      }
    }

    try {
      setSubmitting(true);
      setError('');

      const checkOutData = {
        checkOutBy: user.uid,
        checkOutByName: user.displayName || user.email,
        checkOutNotes: notes,
        pagerRetrieved: pagerRetrieved,
        actualPickupTime: actualPickupTime,
        vidNumber: vidNumber,
        licPlateNumber: licPlateNumber,
        vehicleColor: vehicleColor,
        pickupNotes: pickupNotes
      };

      await checkOutGuest(checkInId, checkOutData);

      // Show success modal; navigate to /check-in when the user dismisses it
      setSubmitting(false);
      setShowCheckedOutConfirm(true);

    } catch (err) {
      console.error('Check-out error:', err);
      setError('Failed to check out guest. Please try again.');
      setSubmitting(false);
    }
  };

  // Get all available phone numbers from the guest profile
  const getGuestPhoneOptions = () => {
    if (!guestData) return [];
    const phones = [];
    const seen = new Set();
    const addPhone = (label, number) => {
      if (!number) return;
      const cleaned = number.replace(/\D/g, '');
      if (cleaned && !seen.has(cleaned)) {
        seen.add(cleaned);
        phones.push({ label, number });
      }
    };
    addPhone('Mobile', guestData.mobilePhone);
    addPhone('Contact', guestData.phone);
    addPhone('Home', guestData.homePhone);
    addPhone('Work', guestData.workPhone);
    return phones;
  };

  const phoneOptions = getGuestPhoneOptions();
  const guestPhone = selectedPhone || (phoneOptions.length > 0 ? phoneOptions[0].number : null);

  const handleSendSms = async () => {
    if (!guestPhone) return;

    try {
      setSendingSms(true);
      setSmsError('');
      setSmsSuccess('');

      await sendTransportationSMS({
        checkInId,
        guestId: guestData?.id || checkInData.guestId,
        guestFirstName: checkInData.guestFirstName,
        guestLastName: checkInData.guestLastName,
        phoneNumber: guestPhone,
        customMessage: editableMessage !== smsMessage ? editableMessage : undefined,
      });

      setSmsSuccess('Transportation notification sent successfully!');
      setShowSmsConfirm(false);
      // Check-in data updates automatically via real-time listener
    } catch (err) {
      console.error('SMS send error:', err);
      setSmsError(err.message || 'Failed to send SMS notification.');
    } finally {
      setSendingSms(false);
    }
  };

  if (loading) {
    return (
      <div className="loading"><div className="spinner"></div><p>Loading...</p></div>
    );
  }

  if (error && !checkInData) {
    return (
      <div className="checkout-container">
        <div className="error-page">
          <h2>Error</h2>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const smsMessage = `Hi ${checkInData.guestFirstName}, your transportation has arrived. Please come to the lobby now. Thank you!`;

  const isCheckedIn = checkInData.status !== 'checked_out';

  const formatTime12h = (timeStr) => {
    if (!timeStr || typeof timeStr !== 'string') return '';
    const parts = timeStr.split(':');
    if (parts.length < 2) return timeStr;
    const h = parseInt(parts[0], 10);
    const mm = parts[1];
    if (isNaN(h)) return timeStr;
    const period = h >= 12 ? 'PM' : 'AM';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${h12}:${mm} ${period}`;
  };

  const hasPickupVehicleDetails = Boolean(
    checkInData.actualPickupTime ||
    checkInData.vidNumber ||
    checkInData.licPlateNumber ||
    checkInData.vehicleColor
  );
  const canCompleteCheckOut = !checkInData.pagerNumber || pagerRetrieved;
  const initials = `${(checkInData.guestFirstName || '').charAt(0)}${(checkInData.guestLastName || '').charAt(0)}`.toUpperCase();
  const rawGuestId = guestData?.id || checkInData.guestId;
  const ccbNumericId = rawGuestId && rawGuestId.startsWith('ccb_')
    ? rawGuestId.slice(4)
    : null;

  return (
    <div className="checkout-page">
      <div className="checkout-back-row">
        <button className="btn-back-link" onClick={() => navigate('/dashboard')}>
          ← Back to Dashboard
        </button>
      </div>

      {error && <div className="message error-message">{error}</div>}
      {smsSuccess && <div className="message success-message">{smsSuccess}</div>}
      {smsError && <div className="message error-message">{smsError}</div>}

      <div className="checkout-page-inner">
        <div className="page-section-row">
          <h1 className="page-section-title">Guest Information</h1>
          {isCheckedIn ? (
            <span className="status-pill status-pill-active">
              <span className="status-pill-dot"></span> Currently Checked In
            </span>
          ) : (
            <span className="status-pill status-pill-out">✓ Checked Out</span>
          )}
        </div>

        <div className="guest-card">
          <div className="guest-card-left">
            <div className="guest-avatar-circle">
              {guestData?.image ? (
                <img
                  src={guestData.image}
                  alt={`${checkInData.guestFirstName} ${checkInData.guestLastName}`}
                />
              ) : initials ? (
                <span className="guest-avatar-initials">{initials}</span>
              ) : (
                <img
                  src={placeholderAvatar}
                  alt={`${checkInData.guestFirstName} ${checkInData.guestLastName}`}
                  className="guest-avatar-placeholder-img"
                />
              )}
            </div>
            <div className="guest-card-text">
              <h2 className="guest-card-name">
                {checkInData.guestFirstName} {checkInData.guestLastName}
              </h2>
              <p className="guest-card-sub">
                Guest
                {ccbNumericId && (
                  <>
                    {' '}&middot;{' '}
                    <a
                      className="guest-card-ccb-link"
                      href={`https://mybt.ccbchurch.com/goto/individuals/${ccbNumericId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open profile in CCB"
                    >
                      CCB ID: {ccbNumericId}
                    </a>
                  </>
                )}
              </p>
            </div>
          </div>
          {(guestData?.phone || guestData?.email) && (
            <div className="guest-card-contact">
              {guestData?.phone && (
                <div className="guest-contact-line">
                  <span className="contact-line-icon" aria-hidden="true">📞</span>
                  <span>{formatPhoneNumber(guestData.phone)}</span>
                </div>
              )}
              {guestData?.email && (
                <div className="guest-contact-line">
                  <span className="contact-line-icon" aria-hidden="true">✉️</span>
                  <span>{guestData.email}</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="stat-tiles">
          <div className="stat-tile">
            <span className="stat-tile-label">Check-In</span>
            <span className="stat-tile-value">
              {checkInData.checkInTime ? formatDate(checkInData.checkInTime, 'MMM d, h:mm a') : '—'}
            </span>
            {checkInData.checkInByName && (
              <span className="stat-tile-sub">by {checkInData.checkInByName}</span>
            )}
          </div>
          <div className="stat-tile">
            <span className="stat-tile-label">Service</span>
            <span className="stat-tile-value">{checkInData.serviceType || '—'}</span>
          </div>
          <div className="stat-tile">
            <span className="stat-tile-label">Pager</span>
            <span className="stat-tile-value">
              {checkInData.pagerNumber ? `#${checkInData.pagerNumber}` : 'N/A'}
            </span>
          </div>
          <div className="stat-tile">
            <span className="stat-tile-label">Duration</span>
            <span className="stat-tile-value">
              {!isCheckedIn && checkInData.checkOutTime
                ? formatDuration(calculateDuration(checkInData.checkInTime, checkInData.checkOutTime))
                : formatDuration(calculateDuration(checkInData.checkInTime, new Date()))}
            </span>
          </div>
        </div>

        <div className="page-subsection">
          <div className="subsection-header">
            <span className="subsection-title">
              Profile Notes <span className="subsection-count">&middot; {profileNotes.length}</span>
            </span>
            {rawGuestId && rawGuestId !== 'new' && (
              <button
                type="button"
                className="link-button"
                onClick={() => setShowNoteModal(true)}
              >
                + Add note
              </button>
            )}
          </div>
          <div className="notes-card">
            {profileNotes.length === 0 ? (
              <p className="notes-empty">No profile notes yet.</p>
            ) : (
              <ul className="profile-notes-list">
                {profileNotes.map((note) => (
                  <li key={note.id} className="profile-note-row">
                    <div className="profile-note-row-top">
                      <p className="profile-note-text">{note.noteText}</p>
                      <span className="profile-note-time">
                        {note.timestamp ? formatDate(note.timestamp, 'MMM d, h:mm a') : ''}
                      </span>
                    </div>
                    <span className="profile-note-author">{note.authorName || 'Unknown'}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {checkInData.checkInNotes && (
          <div className="page-subsection">
            <div className="subsection-header">
              <span className="subsection-title">Check-In Notes</span>
            </div>
            <div className="notes-card">
              <p className="notes-text-large">{checkInData.checkInNotes}</p>
            </div>
          </div>
        )}

        {isCheckedIn && (
          <div className="page-subsection">
            <div className="subsection-header">
              <span className="subsection-title">Check-Out Tasks</span>
            </div>

            <div className="task-card">
              <div className="task-card-icon transport" aria-hidden="true">📞</div>
              <div className="task-card-body">
                <div className="task-card-title">Notify transportation arrived</div>
                {guestPhone ? (
                  <div className="task-card-meta">
                    Text to <strong>{formatPhoneNumber(guestPhone)}</strong>
                    {' '}
                    <span className={`task-meta-status ${checkInData.smsSent ? 'status-done' : 'status-pending'}`}>
                      {checkInData.smsSent ? '· sent' : '· not sent yet'}
                    </span>
                  </div>
                ) : (
                  <div className="task-card-meta task-meta-warning">
                    ⚠️ No phone number on file — update the guest profile to enable SMS.
                  </div>
                )}
                {phoneOptions.length > 1 && (
                  <div className="task-phone-selector">
                    {phoneOptions.map((opt) => (
                      <label
                        key={opt.number}
                        className={`task-phone-option ${guestPhone === opt.number ? 'selected' : ''}`}
                      >
                        <input
                          type="radio"
                          name="taskSmsPhone"
                          value={opt.number}
                          checked={guestPhone === opt.number}
                          onChange={() => setSelectedPhone(opt.number)}
                        />
                        <span className="task-phone-label">{opt.label}</span>
                        <span className="task-phone-number">{formatPhoneNumber(opt.number)}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              {guestPhone && (
                <button
                  type="button"
                  className="btn-task-action"
                  onClick={() => {
                    setSmsError('');
                    setSmsSuccess('');
                    setEditableMessage(smsMessage);
                    setShowSmsConfirm(true);
                  }}
                  disabled={sendingSms || ro.disabled}
                  title={ro.title}
                >
                  {checkInData.smsSent ? 'Resend' : 'Send Text'} →
                </button>
              )}
            </div>

            {checkInData.pagerNumber && (
              <label className={`task-card task-card-warning ${pagerRetrieved ? 'is-done' : ''}`}>
                <span className="task-card-checkbox">
                  <input
                    type="checkbox"
                    checked={pagerRetrieved}
                    onChange={(e) => setPagerRetrieved(e.target.checked)}
                  />
                </span>
                <div className="task-card-body">
                  <div className="task-card-title">Pager #{checkInData.pagerNumber} retrieved</div>
                  <div className="task-card-meta">Required before completing check-out</div>
                </div>
              </label>
            )}
          </div>
        )}

        {smsMessages.length > 0 && (
          <div className="page-subsection">
            <div className="subsection-header">
              <span className="subsection-title">SMS History</span>
            </div>
            <div className="notes-card sms-history-card">
              {(() => {
                const lastOutbound = [...smsMessages].reverse().find(m => m.direction === 'outbound');
                const hasReply = smsMessages.some(m => m.direction === 'inbound');
                if (hasReply) {
                  return (
                    <div className="sms-delivery-banner sms-delivery-read">
                      <span className="sms-delivery-icon">✅</span>
                      <span>Guest has replied - message was received and read</span>
                    </div>
                  );
                } else if (lastOutbound?.viewedAt) {
                  return (
                    <div className="sms-delivery-banner sms-delivery-read">
                      <svg className="sms-eye-icon-banner sms-eye-read" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                      <span>Message read by {checkInData.guestFirstName} - {formatSmsTime(lastOutbound.viewedAt)}</span>
                    </div>
                  );
                } else if (lastOutbound?.status === 'sent') {
                  return (
                    <div className="sms-delivery-banner sms-delivery-sent">
                      <span className="sms-delivery-icon">📤</span>
                      <span>Message sent - waiting for guest to confirm read</span>
                    </div>
                  );
                } else if (lastOutbound?.status === 'failed') {
                  return (
                    <div className="sms-delivery-banner sms-delivery-failed">
                      <span className="sms-delivery-icon">❌</span>
                      <span>Message delivery failed - please try again</span>
                    </div>
                  );
                }
                return null;
              })()}
              <div className="sms-thread">
                {smsMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`sms-bubble ${msg.direction === 'outbound' ? 'sms-outbound' : 'sms-inbound'}`}
                  >
                    <div className="sms-bubble-content">
                      <p className="sms-message-text">{msg.message}</p>
                      <div className="sms-meta">
                        <span className="sms-time">{formatSmsTime(msg.sentAt)}</span>
                        {msg.direction === 'outbound' && (
                          <>
                            <span className="sms-sender">Sent by {msg.sentByName}</span>
                            {msg.status === 'sent' && !msg.viewedAt && (
                              <span className="sms-status-sent">✓ Sent</span>
                            )}
                            {msg.viewedAt && (
                              <span className="sms-delivered-badge" title={`Read at ${formatSmsTime(msg.viewedAt)}`}>
                                <svg className="sms-eye-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                  <circle cx="12" cy="12" r="3"/>
                                </svg>
                                Read
                              </span>
                            )}
                          </>
                        )}
                        {msg.direction === 'inbound' && (
                          <span className="sms-sender">Reply from {msg.guestFirstName}</span>
                        )}
                        {msg.status === 'failed' && (
                          <span className="sms-failed-badge">Failed</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {isCheckedIn ? (
          <form onSubmit={handleCheckOut} className="checkout-form">
            <div className="page-subsection">
              <div className="subsection-header">
                <span className="subsection-title">
                  Pickup &amp; Vehicle Details <span className="optional-marker">— optional</span>
                </span>
              </div>
              <div className="checkout-fields-grid">
                <label className="checkout-field">
                  <span className="checkout-field-label">Actual Pickup Time</span>
                  <input
                    type="time"
                    value={actualPickupTime}
                    onChange={(e) => setActualPickupTime(e.target.value)}
                    className="checkout-field-input"
                  />
                </label>
                <label className="checkout-field">
                  <span className="checkout-field-label">VID #</span>
                  <input
                    type="text"
                    value={vidNumber}
                    onChange={(e) => setVidNumber(e.target.value)}
                    maxLength={12}
                    className="checkout-field-input"
                    placeholder="up to 12 characters"
                  />
                </label>
                <label className="checkout-field">
                  <span className="checkout-field-label">LIC Plate #</span>
                  <input
                    type="text"
                    value={licPlateNumber}
                    onChange={(e) => setLicPlateNumber(e.target.value)}
                    maxLength={10}
                    className="checkout-field-input"
                    placeholder="up to 10 characters"
                  />
                </label>
                <label className="checkout-field">
                  <span className="checkout-field-label">Vehicle Color</span>
                  <input
                    type="text"
                    value={vehicleColor}
                    onChange={(e) => setVehicleColor(e.target.value)}
                    maxLength={12}
                    className="checkout-field-input"
                    placeholder="up to 12 characters"
                  />
                </label>
              </div>
            </div>

            <div className="page-subsection">
              <button
                type="button"
                className="pickup-notes-toggle"
                onClick={() => setPickupNotesOpen((open) => !open)}
                aria-expanded={pickupNotesOpen}
              >
                <span className="subsection-title">
                  Pickup Notes <span className="optional-marker">— optional</span>
                </span>
                <span className={`pickup-notes-chevron${pickupNotesOpen ? ' is-open' : ''}`} aria-hidden="true">▾</span>
              </button>
              {pickupNotesOpen && (
                <>
                  <textarea
                    value={pickupNotes}
                    onChange={(e) => setPickupNotes(e.target.value.slice(0, 140))}
                    maxLength={140}
                    placeholder="Notes about the pickup (max 140 chars)..."
                    rows={3}
                    className="checkout-notes-textarea"
                  />
                  <div className="pickup-notes-counter">{pickupNotes.length}/140</div>
                </>
              )}
            </div>

            <div className="page-subsection">
              <div className="subsection-header">
                <span className="subsection-title">
                  Check-Out Notes <span className="optional-marker">— optional</span>
                </span>
              </div>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any notes about the visit..."
                rows={4}
                className="checkout-notes-textarea"
              />
            </div>

            {!canCompleteCheckOut && (
              <p className="form-hint">
                <span className="form-hint-dot" aria-hidden="true"></span>
                Confirm pager retrieved to continue
              </p>
            )}

            <div className="page-actions">
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => navigate('/dashboard')}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitting || !canCompleteCheckOut || ro.disabled}
                title={ro.title}
              >
                {submitting ? 'Processing...' : 'Complete Check-Out'}
              </button>
            </div>
          </form>
        ) : (
          <>
            {hasPickupVehicleDetails && (
              <div className="page-subsection">
                <div className="subsection-header">
                  <span className="subsection-title">Pickup &amp; Vehicle Details</span>
                </div>
                <div className="pickup-details-grid">
                  {checkInData.actualPickupTime && (
                    <div className="pickup-details-cell">
                      <div className="pickup-details-label">Actual Pickup Time</div>
                      <div className="pickup-details-value">{formatTime12h(checkInData.actualPickupTime)}</div>
                    </div>
                  )}
                  {checkInData.vidNumber && (
                    <div className="pickup-details-cell">
                      <div className="pickup-details-label">VID #</div>
                      <div className="pickup-details-value">{checkInData.vidNumber}</div>
                    </div>
                  )}
                  {checkInData.licPlateNumber && (
                    <div className="pickup-details-cell">
                      <div className="pickup-details-label">LIC Plate #</div>
                      <div className="pickup-details-value">{checkInData.licPlateNumber}</div>
                    </div>
                  )}
                  {checkInData.vehicleColor && (
                    <div className="pickup-details-cell">
                      <div className="pickup-details-label">Vehicle Color</div>
                      <div className="pickup-details-value">{checkInData.vehicleColor}</div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {checkInData.pickupNotes && (
              <div className="page-subsection">
                <div className="subsection-header">
                  <span className="subsection-title">Pickup Notes</span>
                </div>
                <div className="notes-card">
                  <p className="notes-text-large">{checkInData.pickupNotes}</p>
                </div>
              </div>
            )}
            {checkInData.checkOutNotes && (
              <div className="page-subsection">
                <div className="subsection-header">
                  <span className="subsection-title">Check-Out Notes</span>
                </div>
                <div className="notes-card">
                  <p className="notes-text-large">{checkInData.checkOutNotes}</p>
                </div>
              </div>
            )}
            <div className="page-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => navigate('/dashboard')}
              >
                Return to Dashboard
              </button>
            </div>
          </>
        )}
      </div>

      {showNoteModal && (
        <AddNoteModal
          guest={{
            id: checkInData.guestId,
            firstName: checkInData.guestFirstName,
            lastName: checkInData.guestLastName,
            image: guestData?.image,
            email: guestData?.email,
            phone: guestData?.phone,
          }}
          onClose={() => setShowNoteModal(false)}
          onSave={() => {
            setShowNoteModal(false);
            refreshProfileNotes();
          }}
        />
      )}

      {/* Check-Out Success Modal — OK returns to /check-in for the next guest */}
      {showCheckedOutConfirm && (
        <div
          className="confirm-dialog-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => {
            setShowCheckedOutConfirm(false);
            navigate('/check-in');
          }}
        >
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h3 className="confirm-dialog-title">Guest checked out</h3>
            <div className="confirm-dialog-body">
              <strong>{checkInData.guestFirstName} {checkInData.guestLastName}</strong>{' '}
              has been successfully checked out.
            </div>
            <div className="confirm-dialog-actions">
              <button
                type="button"
                className="btn btn-primary"
                autoFocus
                onClick={() => {
                  setShowCheckedOutConfirm(false);
                  navigate('/check-in');
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SMS Confirmation Modal */}
      {showSmsConfirm && (
        <div className="sms-modal-overlay" onClick={() => !sendingSms && setShowSmsConfirm(false)}>
          <div className="sms-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sms-modal-header">
              <h3 className="sms-modal-title">Confirm SMS Notification</h3>
            </div>
            <div className="sms-modal-body">
              <div className="sms-preview-card">
                <div className="sms-preview-label">Message</div>
                <textarea
                  className="sms-edit-textarea"
                  value={editableMessage}
                  onChange={(e) => setEditableMessage(e.target.value)}
                  rows={4}
                  disabled={sendingSms}
                />
                <div className="sms-preview-recipient">
                  <span className="sms-preview-to">To:</span>
                  <span className="sms-preview-phone">
                    {checkInData.guestFirstName} {checkInData.guestLastName} - {formatPhoneNumber(guestPhone)}
                  </span>
                </div>
              </div>
              {smsError && (
                <div className="message error-message" style={{ marginTop: '12px' }}>
                  {smsError}
                </div>
              )}
            </div>
            <div className="sms-modal-actions">
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => setShowSmsConfirm(false)}
                disabled={sendingSms}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-send-sms"
                onClick={handleSendSms}
                disabled={sendingSms || ro.disabled}
                title={ro.title}
              >
                {sendingSms ? 'Sending...' : 'Send Notification'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckOut;
